import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { announce } from "@react-aria/live-announcer";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useIsEditForm } from "@/widgets/classic-form/IsEditFormContext";
import { useFormAPI, useFormStore } from "@/widgets/classic-form/form";
import type { FormValues } from "@/widgets/classic-form/form";
import type { RangeSliderInteractionKind, RangeSliderLabels, RangeSliderValue } from "@/features/range-slider";
import { RangeSlider } from "@/features/range-slider";
import type { PrefixObjectAccessor } from "@/shared/forms";
import { useEffectEvent } from "@/shared/hooks/useEffectEvent";
import { useTrackSearchFormInteraction } from "@/shared/lib/ube";
import { Callout } from "@/shared/ui/ancestry";
import styles from "./TimespanCard.module.scss";

export interface HistoricalPeriod {
    label: string;
    startYear: number;
    endYear: number;
}

export interface TimespanCardProps {
    sliderRange: { min: number; max: number };
    startFieldPath: PrefixObjectAccessor<FormValues, []>;
    endFieldPath: PrefixObjectAccessor<FormValues, []>;
    historicalPeriods?: HistoricalPeriod[];
}

/**
 * TimespanCard — reads/writes form state directly for start/end year.
 * Guards the slider against invalid ranges (start >= end).
 * Slider drag uses internal state for smooth UX, commits to form on drag end.
 */
export function TimespanCard({ sliderRange, startFieldPath, endFieldPath, historicalPeriods = [] }: TimespanCardProps) {
    const { t } = useTranslation();
    const { min, max } = sliderRange;
    const { setFieldValue } = useFormAPI();
    const isEditForm = useIsEditForm();
    const trackInteraction = useTrackSearchFormInteraction();

    const [periodCalloutOpen, setPeriodCalloutOpen] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState<HistoricalPeriod | null>(null);
    const periodTriggerRef = useRef<HTMLButtonElement>(null);

    // Read form state — separate selectors return primitives for stable equality checks
    const startYear = useFormStore(({ values }) => values.timespan.yearStart ?? min);
    const endYear = useFormStore(({ values }) => values.timespan.yearEnd ?? max);

    const minGap = 1;
    const isDirty = startYear !== min || endYear !== max;

    const sliderValue: RangeSliderValue = useMemo(() => ({ start: startYear, end: endYear }), [startYear, endYear]);

    const rangeSliderLabels: RangeSliderLabels = useMemo(
        () => ({
            ariaLabel: t("timespan.rangeSliderAriaLabel"),
            startLabel: t("timespan.startYear"),
            endLabel: t("timespan.endYear"),
            expandStartLabel: t("timespan.showPreviousYears"),
            expandEndLabel: t("timespan.showNextYears"),
            startKnobValueTextKey: "timespan.startKnobValueText",
            endKnobValueTextKey: "timespan.endKnobValueText",
            rangeSummaryKey: "timespan.yearCount",
        }),
        [t],
    );

    const clearHistoricalPeriod = useEffectEvent((start: number, end: number) => {
        if (!selectedPeriod) return;

        const isOutOfBounds = start < min || start > max || end < min || end > max;
        if (isOutOfBounds) return;

        const { startYear, endYear } = selectedPeriod;
        const isDifferentFromSelectedPeriod = start !== startYear || end !== endYear;
        if (isDifferentFromSelectedPeriod) {
            setSelectedPeriod(null);
        }
    });

    useEffect(() => {
        clearHistoricalPeriod(startYear, endYear);
    }, [startYear, endYear]);

    // ─── Handlers ────────────────────────────────────────────────────────────
    const handleSliderChange = useCallback(
        (newValue: RangeSliderValue) => {
            setFieldValue(startFieldPath, newValue.start);
            setFieldValue(endFieldPath, newValue.end);
        },
        [setFieldValue, startFieldPath, endFieldPath],
    );

    const handleSliderInteraction = useCallback(
        (kind: RangeSliderInteractionKind) => {
            trackInteraction({ action: kind, objectName: "estimated_timespan" });
        },
        [trackInteraction],
    );

    const handlePeriodSelect = useCallback(
        (period: HistoricalPeriod) => {
            const isSelected = selectedPeriod?.label === period.label;
            let nextPeriod: HistoricalPeriod | null = period,
                startYear = period.startYear,
                endYear = period.endYear;

            if (isSelected) {
                nextPeriod = null;
                startYear = min;
                endYear = max;
            }

            setSelectedPeriod(nextPeriod);
            setPeriodCalloutOpen(false);
            setFieldValue(startFieldPath, startYear);
            setFieldValue(endFieldPath, endYear);
            trackInteraction({ action: "preset", objectName: "estimated_timespan" });
        },
        [setFieldValue, startFieldPath, endFieldPath, selectedPeriod, min, max, trackInteraction],
    );

    const handleReset = useCallback(() => {
        setSelectedPeriod(null);
        setFieldValue(startFieldPath, min);
        setFieldValue(endFieldPath, max);
        announce(t("timespan.rangeResetTo", { start: min, end: max }), "polite");
        trackInteraction({ action: "reset", objectName: "estimated_timespan" });
    }, [min, max, setFieldValue, startFieldPath, endFieldPath, t, trackInteraction]);

    // ─── Render ──────────────────────────────────────────────────────────────
    return (
        <div className={classNames(styles.card, isEditForm ? styles.isEditForm : "")}>
            <div className={styles.sliderArea}>
                <RangeSlider
                    min={min}
                    max={max}
                    value={sliderValue}
                    onChange={handleSliderChange}
                    onInteraction={handleSliderInteraction}
                    minGap={minGap}
                    labels={rangeSliderLabels}
                />
            </div>

            <div className={styles.footer}>
                <div className={styles.periodSection}>
                    <button
                        ref={periodTriggerRef}
                        type="button"
                        className={classNames("link iconAfter iconArrowSmallDownAfter", styles.periodButton)}
                    >
                        <span>{selectedPeriod?.label ?? t("timespan.historicalPeriod")}</span>
                    </button>

                    <Callout
                        trigger={periodTriggerRef}
                        open={periodCalloutOpen}
                        onOpen={() => setPeriodCalloutOpen(true)}
                        onClose={() => setPeriodCalloutOpen(false)}
                    >
                        <h5 className="calloutMenuTitle">{t("timespan.choosePeriod")}</h5>
                        <ul className="calloutMenu" data-testid="SFS_historicalPeriodCalloutMenu">
                            {historicalPeriods.map((period) => {
                                const isSelected = selectedPeriod?.label === period.label;
                                return (
                                    <li key={period.label}>
                                        <button
                                            type="button"
                                            aria-pressed={isSelected}
                                            className={classNames(
                                                "link iconAfter iconAfterCheck",
                                                isSelected ? "calloutMenuChecked" : "calloutMenuUnchecked",
                                            )}
                                            onClick={() => handlePeriodSelect(period)}
                                        >
                                            {period.label}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </Callout>
                </div>

                <button type="button" className="link" onClick={handleReset} disabled={!isDirty}>
                    {t("timespan.reset")}
                </button>
            </div>
        </div>
    );
}
